import { useEffect, useState } from "react"
import { useReducedMotion } from "framer-motion"

interface ClockGaugeProps {
  value: number
  max: number
  color: string
  size?: number
  delay?: number
  format?: (n: number) => string | number
  valueSize?: number
  denom?: string
}

export function ClockGauge({
  value,
  max,
  color,
  size = 88,
  delay = 0,
  format,
  valueSize = 22,
  denom,
}: ClockGaugeProps) {
  const pct = Math.min(1, Math.max(0, max > 0 ? value / max : 0))
  const cx = size / 2
  const cy = size / 2
  const NUM_MAJOR = 5
  const MINORS_BETWEEN = 12
  const NUM_TOTAL = (NUM_MAJOR - 1) * (MINORS_BETWEEN + 1) + 1
  const START_ANGLE = 135
  const SWEEP = 270
  const OUTER_R = size * 0.42
  const MAJOR_LEN = size * 0.11
  const MINOR_LEN = size * 0.07
  const NEEDLE_INNER = size * 0.3
  const NEEDLE_OUTER = size * 0.46

  const [p, setP] = useState(0)
  const [n, setN] = useState(0)
  const reduce = useReducedMotion()

  useEffect(() => {
    if (reduce) {
      setP(pct)
      setN(value)
      return
    }
    let raf: number
    const start = performance.now() + delay * 1000
    const ease = (t: number) => 1 - Math.pow(1 - t, 3)
    const tick = (now: number) => {
      if (now < start) {
        raf = requestAnimationFrame(tick)
        return
      }
      const t = Math.min(1, (now - start) / 1200)
      setP(pct * ease(t))
      setN(value * ease(t))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [pct, value, delay, reduce])

  const polar = (r: number, deg: number): [number, number] => {
    const rad = (deg * Math.PI) / 180
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)]
  }

  const ticks = []
  for (let i = 0; i < NUM_TOTAL; i++) {
    const t = i / (NUM_TOTAL - 1)
    const deg = START_ANGLE + t * SWEEP
    const isMajor = i % (MINORS_BETWEEN + 1) === 0
    const filled = t <= p + 0.0001
    const len = isMajor ? MAJOR_LEN : MINOR_LEN
    const inner = OUTER_R - len
    const [x1, y1] = polar(inner, deg)
    const [x2, y2] = polar(OUTER_R, deg)
    const tickColor = filled ? color : isMajor ? "rgba(255,255,255,0.20)" : "rgba(255,255,255,0.09)"
    ticks.push(
      <line
        key={i}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={tickColor}
        strokeWidth={isMajor ? 2 : 1.4}
        strokeLinecap="round"
        style={filled ? { filter: `drop-shadow(0 0 2.5px ${color}aa)` } : undefined}
      />
    )
  }
  const needleDeg = START_ANGLE + p * SWEEP
  const [nx1, ny1] = polar(NEEDLE_INNER, needleDeg)
  const [nx2, ny2] = polar(NEEDLE_OUTER, needleDeg)

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ display: "block", overflow: "visible" }}>
        {ticks}
        <line
          x1={nx1}
          y1={ny1}
          x2={nx2}
          y2={ny2}
          stroke="#fff"
          strokeWidth={2.4}
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 4px #fff) drop-shadow(0 0 8px ${color})` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="font-bold tabular-nums"
          style={{ fontSize: valueSize, color: "#f8fafc", lineHeight: 1, letterSpacing: -1.2, textShadow: `0 0 10px ${color}80` }}
        >
          {format ? format(n) : Math.round(n)}
        </div>
        {denom && (
          <div className="font-semibold" style={{ fontSize: 9, color: "#64748b", marginTop: 2 }}>
            {denom}
          </div>
        )}
      </div>
    </div>
  )
}
